import { inject, Injectable, OnDestroy } from '@angular/core';
import { LenderService } from '@shared/service/lender/lender.service';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { LogicFnContext } from '@workflow/service/logic/logic.service';
import { OaoPageHelper } from '../../helper/oao-page.helper';
import { TokenService } from '@cudirect/shared-ui-library';
import { Subscription } from 'rxjs';
import { StatefulService } from '@app/service/stateful.service';

/**
 * Methods on this service get registered as "logic functions" on LogicService
 */
@Injectable({ providedIn: 'root' })
export class OaoVisibleFunctionsService implements OnDestroy {
  private readonly statefulService = inject(StatefulService);

  private readonly subscriptions = new Subscription();
  private token = null;

  constructor(
    private readonly flowHelper: OaoPageHelper,
    private readonly lenderService: LenderService,
    private readonly adminPortalFeaturesService: AdminPortalFeaturesService,
    private readonly tokenService: TokenService
  ) {
    const tokenSub = this.tokenService.getToken().subscribe((token) => {
      this.token = token;
    });
    this.subscriptions.add(tokenSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public isUserAuthenticated = (context: LogicFnContext): boolean => {
    return this.flowHelper.isUserAuthenticated;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public isUserNotAuthenticated = (context: LogicFnContext): boolean => {
    return !this.flowHelper.isUserAuthenticated;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public isOAOInstatouchEnabled = (context: LogicFnContext): boolean => {
    const adminPortalFeatures = this.adminPortalFeaturesService?.config;
    return adminPortalFeatures?.oaoFeatures.enableInstatouch;
  };

  public showOAOCard = (context: LogicFnContext): boolean => {
    const cardId = context.card.id;
    const lstExcludedCards =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.lenderService && this.lenderService.lenderDataInfo && (this.lenderService.lenderDataInfo.excludedCards as any[]);

    if (lstExcludedCards && lstExcludedCards.indexOf(cardId) !== -1) {
      return false;
    }

    return true;
  };

  public showBeneficiarySameAsPrimary = (context: LogicFnContext, primaryAddressPath: string, usResidentPath: string): boolean => {
    const addressLength = this.statefulService.arraySize(primaryAddressPath);
    if (addressLength > 0) {
      const isUsResident = this.statefulService.wrap(usResidentPath).value;
      return !isUsResident;
    }
    return true;
  };

  /** Method to check any of bsa (Products & Services Offerings) checkbox checked */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public showBSAProductserviceHasValue = (context: LogicFnContext): boolean => {
    const storagePathPrefix = 'api:oaoMemberInformation/businessInformation/bankSecrecyAct/productsAndServicesOfferingsList';
    const checkcashing = this.statefulService.wrap(`${storagePathPrefix}/checkcashing`).value as boolean;
    const moneytransmission = this.statefulService.wrap(`${storagePathPrefix}/moneytransmission`).value as boolean;
    const currencyorVirtualCurrency = this.statefulService.wrap(`${storagePathPrefix}/currencyorVirtualCurrency`).value as boolean;
    const saleorStoredCards = this.statefulService.wrap(`${storagePathPrefix}/saleorStoredCards`).value as boolean;
    const issuerorSeller = this.statefulService.wrap(`${storagePathPrefix}/issuerorSeller`).value as boolean;
    if (checkcashing || moneytransmission || currencyorVirtualCurrency || saleorStoredCards || issuerorSeller) {
      return true;
    }
    return false;
  };

  /**
   * hide Account and Service Previous Back Button
   * @param context
   * @returns
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public hideAccountAndServicePreviousBackButton = (context: LogicFnContext): boolean => {
    const bundleValue = this.statefulService.wrap('api:oaoMemberInformation/isMidAppDecisionEnabled').value;
    return typeof bundleValue == 'boolean' ? bundleValue : false;
  };

  /**
   * method to hide eligibility back button
   * @param context
   * @returns
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public hideEligibilityBackButton = (context: LogicFnContext): boolean => {
    const bundleMembershipId = this.statefulService.wrap('mem:oaoMemberInformation/membership/membershipID').value;
    const dropdownMembershipId = this.statefulService.wrap('ss:oaoMemberInformation/membership/dropdownMembershipID').value;
    const membershipID = bundleMembershipId ? bundleMembershipId : dropdownMembershipId;

    const isHideMembershipSelection = this.adminPortalFeaturesService.config?.oaoConfiguration?.memberships?.filter(
      (item) => item.membershipID === +membershipID
    )[0]?.isHideMembershipSelection;

    if (isHideMembershipSelection) {
      return true;
    }
    return false;
  };

  public showOaoClarification = (context: LogicFnContext, phoneType: string): boolean => {
    const contactInfo = this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/phoneType').value;
    if (contactInfo !== phoneType) {
      return false;
    }

    return !this.token;

    // const isOidTokenExist = (): boolean => {
    //   return this.tokenService.getCachedToken() !== null;
    // };
    // return !isOidTokenExist();
  };
}
